import { useLockStore } from "../store/lockStore";
import { useLatest } from "./useLatest";
import { useSignalR } from "./useSignalR";
import { useClaimStuckSelfHeal } from "./lock/useClaimStuckSelfHeal";
import { useGrantTransition } from "./lock/useGrantTransition";
import { useLockCountdown } from "./lock/useLockCountdown";
import { useLockHeartbeat } from "./lock/useLockHeartbeat";
import { useLockLifecycle } from "./lock/useLockLifecycle";
import { useLockSignalREvents } from "./lock/useLockSignalREvents";
import { usePreemptiveSave } from "./lock/usePreemptiveSave";
import { useLockScreenPoll } from "./lock/useLockScreenPoll";
import { useReconcileOnReconnect } from "./lock/useReconcileOnReconnect";
import { useSelfReclaimRetry } from "./lock/useSelfReclaimRetry";

interface UseLockManagerOptions {
  /** Skip the entire lock flow for read-only forms (submitted / new-form-not-yet-saved). */
  enabled: boolean;
  /**
   * Called when this client becomes the lock holder via grant or auto-grant
   * (NOT for the initial acquire on mount). The page uses this to re-fetch
   * form data so the new holder sees whatever the previous holder saved on
   * their way out — otherwise the requester would render with whatever data
   * they cached at initial page load, pre-edit. Returns a Promise the lock
   * manager awaits before transitioning lockStatus to "locked" so the form
   * only renders once with fresh data.
   */
  onLockGranted?: () => Promise<void>;
}

/**
 * Mounts the lock lifecycle on a form page. Thin orchestrator that composes
 * focused sub-hooks (`src/hooks/lock/*`); each sub-hook owns exactly one
 * effect. Behavior matches the pre-split implementation 1:1 — the split is
 * mechanical, no timing or state changes.
 *
 * The component reads `lockStatus`, `lockedBy`, `incomingRequest`, etc. from
 * `useLockStore` and decides what to render (form vs LockScreen vs RemovedScreen).
 */
export function useLockManager(formId: string | null, options: UseLockManagerOptions) {
  const { enabled, onLockGranted } = options;
  const connection = useSignalR(enabled ? formId : null);

  // Long-lived event handlers (SignalR subscriptions, beforeunload listener,
  // grant-transition setTimeout) read these from refs so they don't have to
  // re-register every time the underlying value changes.
  const onLockGrantedRef = useLatest(onLockGranted);

  const sessionId = useLockStore((s) => s.sessionId);
  const lockStatus = useLockStore((s) => s.lockStatus);
  const lockedBy = useLockStore((s) => s.lockedBy);
  const incomingRequest = useLockStore((s) => s.incomingRequest);
  const countdownSeconds = useLockStore((s) => s.countdownSeconds);

  const formIdRef = useLatest(formId);
  const sessionIdRef = useLatest(sessionId);

  const { startGrantedTransition, resetIdempotency } = useGrantTransition(formIdRef, sessionIdRef, onLockGrantedRef);

  useLockLifecycle(enabled, formId, sessionId, formIdRef, sessionIdRef);
  useLockHeartbeat(enabled, formId, sessionId, lockStatus);
  useLockCountdown();
  usePreemptiveSave(enabled, lockStatus, incomingRequest, countdownSeconds);
  useClaimStuckSelfHeal(enabled, lockStatus, countdownSeconds, formIdRef, sessionIdRef, startGrantedTransition, resetIdempotency);
  useLockSignalREvents(connection, formId, formIdRef, sessionIdRef, onLockGrantedRef, startGrantedTransition);
  useReconcileOnReconnect(connection, formIdRef, sessionIdRef, onLockGrantedRef, startGrantedTransition);
  useSelfReclaimRetry(enabled, formId, sessionId, lockStatus, lockedBy);
  useLockScreenPoll(enabled, formId, sessionId, lockStatus, formIdRef, sessionIdRef, onLockGrantedRef);
}
